import { memo, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { UIButton } from 'components/ui/Button'
import { Icon } from 'components/ui/Icon'
import { StatusChip } from 'components/ui/StatusChip'
import { messagesSelectors, setAgentActionStatus } from 'store/messages'
import { confirmAgentAction } from 'api/agents'
import { showErrorToast } from 'utils/toast'
import { AgentAction } from 'types/agent'
import { AgentActionStatusEnum } from 'types/chat-message'

type AgentActionCardProps = {
  action: AgentAction
  /** True once the bot streamed a continuation after the action — i.e. it resolved. */
  resolved: boolean
}

const formatValue = (value: unknown): string => {
  if (value == null) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/**
 * Inline approval card for a paused agent write action (HITL). Shows a readable
 * recap of the action (its `summary`, falling back to params when absent) and
 * lets the user approve or reject — no HTTP verbs, endpoints or raw ids. On
 * decision it relays to the backend; the bot's continuation streams onto the
 * same message. Once concluded it collapses to a title + accepted/rejected badge,
 * expandable for the recap.
 */
const AgentActionCardComponent = ({ action, resolved }: AgentActionCardProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const chatId = useSelector(messagesSelectors.chatId)
  const serverStatus = useSelector(messagesSelectors.agentActionStatusByRun(action.runId))
  const [submitting, setSubmitting] = useState<AgentActionStatusEnum | null>(null)
  // Live messages carry no agentRunId yet, so the server status only lands after a
  // reload — remember our own decision so the badge shows immediately.
  const [decided, setDecided] = useState<AgentActionStatusEnum | null>(null)

  const status = serverStatus ?? decided
  const accepted = status === AgentActionStatusEnum.ACCEPTED
  const rejected = status === AgentActionStatusEnum.REJECTED
  const settled = accepted || rejected

  // Once the action is concluded (bot continued or decided), collapse it.
  const concluded = resolved || settled
  const [expanded, setExpanded] = useState(false)
  const showDetails = !concluded || expanded

  const params = useMemo(() => Object.entries(action.params ?? {}), [action.params])
  const title = concluded ? action?.tool : t('agentActionTitle')

  const decide = async (approved: boolean) => {
    if (!chatId || settled || submitting) return
    const status = approved ? AgentActionStatusEnum.ACCEPTED : AgentActionStatusEnum.REJECTED
    setSubmitting(status)
    try {
      await confirmAgentAction({ chatId, runId: action.runId, approved })
      setDecided(status)
      dispatch(setAgentActionStatus({ runId: action.runId, status }))
    } catch (e) {
      console.error('[agentic] confirm failed', e)
      showErrorToast(t('agentActionError'))
      setSubmitting(null)
    }
  }

  return (
    <div className="tw-my-1 tw-flex tw-flex-col tw-gap-2">
      <div
        className={clsx('tw-flex tw-items-center tw-gap-2', concluded && 'tw-cursor-pointer tw-select-none')}
        role={concluded ? 'button' : undefined}
        aria-expanded={concluded ? expanded : undefined}
        onClick={concluded ? () => setExpanded((value) => !value) : undefined}
      >
        <StatusChip circle variant="info" icon={['fas', 'wand-magic-sparkles']} />
        <span className="tw-min-w-0 tw-truncate tw-text-sm tw-font-semibold" style={{ color: 'var(--text-primary)' }}>
          {title}
        </span>
        {settled && (
          <StatusChip
            className="tw-ml-auto"
            variant={accepted ? 'positive' : 'negative'}
            icon={['far', accepted ? 'check-circle' : 'times-circle']}
            label={t(accepted ? 'agentActionApproved' : 'agentActionRejected')}
          />
        )}
        {concluded && (
          <Icon
            icon={['far', 'chevron-down']}
            className={clsx(
              'tw-h-3 tw-w-3 tw-shrink-0 tw-transition-transform tw-duration-300 motion-reduce:tw-transition-none',
              !settled && 'tw-ml-auto',
              expanded && 'tw-rotate-180',
            )}
            style={{ color: 'var(--text-tertiary)' }}
          />
        )}
      </div>

      <div
        className="tw-grid tw-transition-all tw-duration-300 tw-ease-out motion-reduce:tw-transition-none"
        style={{ gridTemplateRows: showDetails ? '1fr' : '0fr' }}
      >
        <div className="tw-flex tw-min-h-0 tw-flex-col tw-gap-2 tw-overflow-hidden">
          {action.summary ? (
            <p className="tw-m-0 tw-text-sm tw-leading-snug" style={{ color: 'var(--text-secondary)' }}>
              {action.summary}
            </p>
          ) : (
            params.length > 0 && (
              <dl className="tw-m-0 tw-flex tw-flex-col tw-gap-2">
                {params.map(([key, value]) => (
                  <div key={key} className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5 tw-text-xs tw-leading-snug">
                    <dt className="tw-font-semibold tw-break-words" style={{ color: 'var(--text-secondary)' }}>
                      {key}
                    </dt>
                    <dd className="tw-m-0 tw-break-words" style={{ color: 'var(--text-primary)' }}>
                      {formatValue(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            )
          )}

          {!resolved && !settled && (
            <div className="tw-flex tw-gap-1 tw-pt-1">
              <UIButton
                size="sm"
                disabled={!!submitting}
                loading={submitting === AgentActionStatusEnum.ACCEPTED}
                onClick={() => decide(true)}
              >
                {t('agentActionApprove')}
              </UIButton>
              <UIButton
                size="sm"
                variant="secondary"
                disabled={!!submitting}
                loading={submitting === AgentActionStatusEnum.REJECTED}
                onClick={() => decide(false)}
              >
                {t('agentActionReject')}
              </UIButton>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export const AgentActionCard = memo(AgentActionCardComponent)
